import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import BusinessIcon from '@mui/icons-material/BusinessOutlined'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import KeyIcon from '@mui/icons-material/KeyOutlined'
import MailIcon from '@mui/icons-material/MarkEmailReadOutlined'
import LockIcon from '@mui/icons-material/LockOutlined'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'

import { useAuth, type LoginResult, type Membership } from '../auth/AuthContext'
import { tokens } from '../theme'

type LocationState = { from?: { pathname: string } }

type Mode = 'otp' | 'password'
type Step = 'email' | 'code' | 'pick'

const RESEND_COOLDOWN_SECONDS = 30

export default function LoginPage() {
  const { accessToken, login, requestOtp, verifyOtp, switchMembership, loading, error } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as LocationState | null)?.from?.pathname ?? '/'

  const [mode, setMode] = useState<Mode>('otp')
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [pending, setPending] = useState<LoginResult | null>(null)
  const codeRef = useRef<HTMLInputElement | null>(null)

  // Already-authed users bounce straight to their intended destination.
  if (accessToken && step !== 'pick') {
    return <Navigate to={from} replace />
  }

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  useEffect(() => {
    if (step === 'code') {
      const t = setTimeout(() => codeRef.current?.focus(), 40)
      return () => clearTimeout(t)
    }
  }, [step])

  const messageToShow = submitError ?? error?.message ?? null

  async function completeLogin(result: LoginResult) {
    if (result.needs_pick) {
      setPending(result)
      setStep('pick')
      return
    }
    // Single-membership user → land wherever they were headed (or the
    // right default for their active workspace).
    const landing = result.user.active?.is_platform ? '/platform/tenants' : from
    navigate(landing, { replace: true })
  }

  async function onRequestOtp(e?: FormEvent) {
    e?.preventDefault()
    if (!email) { setSubmitError('Enter your email address'); return }
    setSubmitError(null)
    try {
      await requestOtp(email.trim())
      setStep('code')
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } catch { /* AuthContext.error carries the message */ }
  }

  async function onVerifyOtp(e?: FormEvent) {
    e?.preventDefault()
    if (code.length < 6) { setSubmitError('Enter the 6-digit code from your email'); return }
    setSubmitError(null)
    try {
      const r = await verifyOtp(email.trim(), code)
      await completeLogin(r)
    } catch { /* AuthContext.error carries the message */ }
  }

  async function onPasswordLogin(e?: FormEvent) {
    e?.preventDefault()
    if (!email || !password) { setSubmitError('Enter email and password'); return }
    setSubmitError(null)
    try {
      const r = await login(email.trim(), password)
      await completeLogin(r)
    } catch { /* AuthContext.error carries the message */ }
  }

  async function onPickMembership(m: Membership) {
    if (loading) return
    setSubmitError(null)
    try {
      // If the chosen membership already matches the token issued at login,
      // skip the round-trip.
      if (pending && pending.user.active.tenant_id === m.tenant_id && pending.user.active.role === m.role) {
        const landing = m.is_platform ? '/platform/tenants' : from
        navigate(landing, { replace: true })
        return
      }
      const u = await switchMembership(m.tenant_id, m.role)
      void qc.invalidateQueries()
      const landing = u.active?.is_platform ? '/platform/tenants' : from
      navigate(landing, { replace: true })
    } catch { /* AuthContext.error carries the message */ }
  }

  function backToEmail() {
    setStep('email'); setCode(''); setSubmitError(null)
  }

  const cardTitle = (() => {
    if (step === 'pick') return 'Choose a workspace'
    if (mode === 'otp' && step === 'email') return 'Sign in with a code'
    if (mode === 'otp' && step === 'code') return 'Enter your code'
    return 'Sign in with password'
  })()

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: 2,
        background: (t) => `
          radial-gradient(ellipse 800px 500px at 20% 10%, ${alpha(tokens.indigoDark, 0.85)} 0%, transparent 55%),
          radial-gradient(ellipse 700px 400px at 90% 90%, ${alpha(tokens.gold, 0.08)} 0%, transparent 50%),
          ${t.palette.background.default}
        `,
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 56, height: 56, borderRadius: 2, mx: 'auto', mb: 1.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `linear-gradient(135deg, ${tokens.gold} 0%, #D4931A 100%)`,
              }}
            >
              <Typography sx={{ fontWeight: 800, color: tokens.indigoDark, fontSize: 18, letterSpacing: '0.02em' }}>
                QEA
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              FieldLens
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.06em' }}>
              an Insight Delivered product
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '1rem' }}>
              {cardTitle}
            </Typography>

            {messageToShow && (
              <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
                {messageToShow}
              </Alert>
            )}

            {/* Workspace picker step — after successful auth with >1 membership. */}
            {step === 'pick' && pending && (
              <Stack spacing={1.25}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  You have access to {pending.user.memberships.length} workspaces. Pick which to enter — you can switch later from the profile menu.
                </Typography>
                <List sx={{ border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1.5, p: 0 }}>
                  {pending.user.memberships.map((m, i) => (
                    <ListItemButton
                      key={m.tenant_id + m.role}
                      disabled={loading}
                      onClick={() => onPickMembership(m)}
                      sx={{ py: 1.25, borderTop: i === 0 ? 'none' : (t) => `1px solid ${t.palette.divider}` }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {m.is_platform
                          ? <AdminIcon sx={{ color: tokens.gold }} />
                          : <BusinessIcon sx={{ color: tokens.indigo }} />
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {m.is_platform ? 'Platform' : (m.tenant_name || m.tenant_slug)}
                            </Typography>
                            <Chip
                              size="small"
                              label={m.role.replace(/_/g, ' ')}
                              sx={{
                                textTransform: 'capitalize',
                                height: 18,
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                bgcolor: alpha(m.is_platform ? tokens.gold : tokens.indigo, 0.14),
                                color: m.is_platform ? tokens.gold : tokens.indigo,
                              }}
                            />
                          </Stack>
                        }
                        secondary={
                          m.is_platform
                            ? 'All tenants · platform-level configuration'
                            : `Workspace ${m.tenant_slug}`
                        }
                        slotProps={{
                          secondary: { sx: { fontSize: '0.7rem', color: 'text.secondary' } },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
                {loading && (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', pt: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Switching…
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}

            {/* OTP · step 1 — email → send code */}
            {step === 'email' && mode === 'otp' && (
              <Stack spacing={2} component="form" onSubmit={onRequestOtp}>
                <TextField
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={loading || !email}
                  startIcon={loading ? <CircularProgress size={16} /> : <MailIcon />}
                  sx={{ fontWeight: 700 }}
                >
                  {loading ? 'Sending code…' : 'Email me a code'}
                </Button>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  sx={{ textAlign: 'center' }}
                  onClick={() => { setMode('password'); setSubmitError(null) }}
                >
                  Or sign in with a password
                </Link>
              </Stack>
            )}

            {/* OTP · step 2 — verify 6-digit code */}
            {step === 'code' && mode === 'otp' && (
              <Stack spacing={2} component="form" onSubmit={onVerifyOtp}>
                <Alert severity="info" icon={<MailIcon fontSize="small" />} sx={{ py: 0.5 }}>
                  A 6-digit code has been sent to <strong>{email}</strong>.
                </Alert>
                <TextField
                  inputRef={codeRef}
                  label="6-digit code"
                  autoComplete="one-time-code"
                  fullWidth
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      pattern: '[0-9]{6}',
                      style: { fontSize: '1.35rem', letterSpacing: '8px', textAlign: 'center', fontFamily: 'ui-monospace, monospace' },
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={loading || code.length < 6}
                  startIcon={loading ? <CircularProgress size={16} /> : <LockIcon />}
                  sx={{ fontWeight: 700 }}
                >
                  {loading ? 'Verifying…' : 'Sign in'}
                </Button>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Link component="button" type="button" variant="body2" onClick={backToEmail}>
                    Wrong email?
                  </Link>
                  {cooldown > 0 ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Resend in {cooldown}s
                    </Typography>
                  ) : (
                    <Link component="button" type="button" variant="body2" onClick={onRequestOtp}>
                      Resend code
                    </Link>
                  )}
                </Stack>
              </Stack>
            )}

            {/* Password fallback */}
            {step === 'email' && mode === 'password' && (
              <Stack spacing={2} component="form" onSubmit={onPasswordLogin}>
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                <TextField
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  required
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={loading || !email || !password}
                  startIcon={loading ? <CircularProgress size={16} /> : <KeyIcon />}
                  sx={{ fontWeight: 700 }}
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Link component={RouterLink} to="/forgot-password" variant="body2">
                    Forgot password?
                  </Link>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => { setMode('otp'); setSubmitError(null); setPassword('') }}
                  >
                    Use email code instead
                  </Link>
                </Stack>
              </Stack>
            )}
          </Paper>

          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', display: 'block' }}>
            Internal-only · Insight Delivered Ltd
          </Typography>
        </Stack>
      </Container>
    </Box>
  )
}
